<script lang="ts">
 /**
 * Pill-shaped toggle for switching between Item and Container capture modes.
 *
 * @remarks
 * Uses `role="radiogroup"` with `role="radio"` buttons for accessibility.
 * Dark chrome variant (white-on-dark) designed for the camera UI context.
 *
 * **Visual states:**
 * - Active mode gets a solid white pill with dark text and a subtle shadow
 * - Inactive mode is translucent white text that brightens on hover
 */
</script>

<script setup lang="ts">
import type { CaptureMode } from "@/composables/useCaptureFlow"
import { ICON } from "@/lib/icons"

defineProps<{
  /** The currently active capture mode. */
  mode: CaptureMode
}>()

defineEmits<{
  /** Fired when the user taps a mode option. */
  "update:mode": [value: CaptureMode]
}>()
</script>

<!-- Dark chrome variant for camera UI context. -->
<template>
  <div
    role="radiogroup"
    aria-label="Capture mode"
    class="inline-flex items-center rounded-full bg-white/20 p-0.5"
  >
    <button
      role="radio"
      :aria-checked="mode === 'items'"
      class="flex items-center gap-1 rounded-full px-4 py-2 text-xs font-medium transition-colors"
      :class="
        mode === 'items' ? 'bg-white text-neutral-900 shadow-sm' : 'text-white/60 hover:text-white'
      "
      @click="$emit('update:mode', 'items')"
    >
      <UIcon :name="ICON.item" class="text-sm" />
      Item
    </button>
    <button
      role="radio"
      :aria-checked="mode === 'container'"
      class="flex items-center gap-1 rounded-full px-4 py-2 text-xs font-medium transition-colors"
      :class="
        mode === 'container'
          ? 'bg-white text-neutral-900 shadow-sm'
          : 'text-white/60 hover:text-white'
      "
      @click="$emit('update:mode', 'container')"
    >
      <UIcon :name="ICON.container" class="text-sm" />
      Container
    </button>
  </div>
</template>
